'use client';

import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { toast } from 'react-hot-toast';
import { useAnnouncement } from '@/hooks/useAnnouncement';
import { focusManagement } from '@/utils/focusManagement';
import { isPublicRoute } from '@/utils/routeConstants';
import type { 
  BudgetFile, 
  BudgetFileContext, 
  CreateBudgetFileData, 
  UpdateBudgetFileData 
} from '@/libs/types';

// Create the context
const BudgetFileContextProvider = createContext<BudgetFileContext | null>(null);

// Hook to use the context
export const useBudgetFile = () => {
  const context = useContext(BudgetFileContextProvider);
  if (!context) {
    throw new Error('useBudgetFile must be used within a BudgetFileProvider');
  }
  return context;
};

// Local storage key for persisting budget file selection
const BUDGET_FILE_STORAGE_KEY = 'budget_buddies_active_budget_file';

interface BudgetFileProviderProps {
  children: React.ReactNode;
  initialBudgetFiles?: BudgetFile[];
  initialCurrentFile?: BudgetFile | null;
}

export const BudgetFileProvider: React.FC<BudgetFileProviderProps> = ({
  children,
  initialBudgetFiles = [],
  initialCurrentFile = null,
}) => {
  const [budgetFiles, setBudgetFiles] = useState<BudgetFile[]>(initialBudgetFiles);
  const [currentBudgetFile, setCurrentBudgetFile] = useState<BudgetFile | null>(initialCurrentFile);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const router = useRouter();
  const pathname = usePathname();
  const { announce, AnnouncementRegion } = useAnnouncement();

  // Restore focus after page refresh
  useEffect(() => {
    const focusContext = focusManagement.retrieveFocusContext();
    if (focusContext && focusContext.action === 'budget-file-switch') {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        focusManagement.restoreFocus(focusContext);
        announce('Budget file switched successfully');
      }, 200);
    }
  }, [announce]);

  // Memoize public route check to avoid recalculation on every render
  const isOnPublicRoute = useMemo(() => {
    return isPublicRoute(pathname);
  }, [pathname]);

  // Initialize budget files and current file on mount
  useEffect(() => {
    // Skip initialization on public routes
    if (isOnPublicRoute) {
      return;
    }

    const initializeBudgetFiles = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch all budget files
        const response = await fetch('/api/budget-files', {
          credentials: 'include'
        });
        if (!response.ok) {
          throw new Error('Failed to fetch budget files');
        }

        const files = await response.json();
        setBudgetFiles(files);

        // Determine which budget file to set as current
        let activeFile: BudgetFile | null = null;

        if (files.length > 0) {
          // Try to restore from localStorage first
          const savedFileId = localStorage.getItem(BUDGET_FILE_STORAGE_KEY);
          if (savedFileId) {
            activeFile = files.find((f: BudgetFile) => f.id === savedFileId) || null;
          }

          // Fall back to default budget file
          if (!activeFile) {
            activeFile = files.find((f: BudgetFile) => f.is_default) || files[0];
          }
        }

        setCurrentBudgetFile(activeFile);
        
        // Save to localStorage if we have an active file
        if (activeFile) {
          localStorage.setItem(BUDGET_FILE_STORAGE_KEY, activeFile.id);
          // Note: Cookie is set server-side when switching budget files
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load budget files';
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    // Only initialize if we don't have initial data
    if (initialBudgetFiles.length === 0) {
      initializeBudgetFiles();
    } else if (initialCurrentFile) {
      // If we have initial current file, save it to localStorage
      localStorage.setItem(BUDGET_FILE_STORAGE_KEY, initialCurrentFile.id);
      // Note: Cookie should already be set from previous session
    }
  }, [initialBudgetFiles.length, initialCurrentFile, isOnPublicRoute]);

  // Switch to a different budget file
  const switchBudgetFile = useCallback(async (budgetFileId: string) => {
    if (currentBudgetFile?.id === budgetFileId) {
      return; // Already active
    }

    setLoading(true);
    setError(null);

    try {
      // Save focus context before switching with action identifier
      const focusContext = { ...focusManagement.saveFocus(), action: 'budget-file-switch' };
      focusManagement.storeFocusContext(focusContext);
      
      // Announce to screen readers immediately
      announce(`Switching to budget file. Please wait...`);
      
      // Validate the switch with the API
      const response = await fetch('/api/budget-files/switch', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ budget_file_id: budgetFileId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to switch budget file');
      }

      const { budget_file } = await response.json();
      
      // Update current budget file
      setCurrentBudgetFile(budget_file);
      
      // Persist to localStorage for client-side state
      localStorage.setItem(BUDGET_FILE_STORAGE_KEY, budgetFileId);
      
      // Cookie is now set server-side by the API
      toast.success(`Switched to ${budget_file.name}`);
      
      // Announce success to screen readers
      announce(`Successfully switched to ${budget_file.name} budget file`);
      
      // Force a router refresh to ensure server components re-render with new cookie
      // Small delay to ensure cookie is properly set
      setTimeout(() => {
        router.refresh();
      }, 100);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to switch budget file';
      setError(errorMessage);
      toast.error(errorMessage);
      announce(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  }, [currentBudgetFile?.id, router, announce]);

  // Create a new budget file
  const createBudgetFile = useCallback(async (data: CreateBudgetFileData): Promise<BudgetFile> => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/budget-files', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create budget file');
      }

      const newBudgetFile = await response.json();
      
      // Add to budget files list
      setBudgetFiles(prev => [...prev, newBudgetFile]);
      
      // If this is set as default or we have no current file, switch to it
      if (data.is_default || !currentBudgetFile) {
        setCurrentBudgetFile(newBudgetFile);
        localStorage.setItem(BUDGET_FILE_STORAGE_KEY, newBudgetFile.id);
      }
      
      toast.success(`Created budget file: ${newBudgetFile.name}`);
      return newBudgetFile;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create budget file';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [currentBudgetFile]);

  // Update an existing budget file
  const updateBudgetFile = useCallback(async (data: UpdateBudgetFileData): Promise<BudgetFile> => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/budget-files/${data.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update budget file');
      }

      const updatedBudgetFile = await response.json();
      
      // Update in budget files list
      setBudgetFiles(prev => 
        prev.map(file => file.id === data.id ? updatedBudgetFile : file)
      );
      
      // Update current file if it's the one being edited
      if (currentBudgetFile?.id === data.id) {
        setCurrentBudgetFile(updatedBudgetFile);
      }
      
      toast.success(`Updated budget file: ${updatedBudgetFile.name}`);
      return updatedBudgetFile;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update budget file';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [currentBudgetFile]);

  // Delete a budget file
  const deleteBudgetFile = useCallback(async (budgetFileId: string, transferTo?: string) => {
    setLoading(true);
    setError(null);

    try {
      const url = transferTo 
        ? `/api/budget-files/${budgetFileId}?transferTo=${transferTo}`
        : `/api/budget-files/${budgetFileId}`;

      const response = await fetch(url, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete budget file');
      }

      const { transferredTo } = await response.json();
      
      // Remove from budget files list
      const remainingFiles = budgetFiles.filter(file => file.id !== budgetFileId);
      setBudgetFiles(remainingFiles);
      
      // If we deleted the current file, switch to another one
      if (currentBudgetFile?.id === budgetFileId) {
        const newCurrentFile = transferTo
          ? remainingFiles.find(file => file.id === transferTo)
          : remainingFiles.find(file => file.is_default) || remainingFiles[0];
          
        setCurrentBudgetFile(newCurrentFile || null);
        
        if (newCurrentFile) {
          localStorage.setItem(BUDGET_FILE_STORAGE_KEY, newCurrentFile.id);
        } else {
          localStorage.removeItem(BUDGET_FILE_STORAGE_KEY);
        }
      }
      
      const message = transferredTo 
        ? 'Budget file deleted and data transferred successfully'
        : 'Budget file deleted successfully';
        
      toast.success(message);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete budget file';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [budgetFiles, currentBudgetFile]);

  // Context value
  const value: BudgetFileContext = {
    currentBudgetFile,
    budgetFiles,
    switchBudgetFile,
    createBudgetFile,
    updateBudgetFile,
    deleteBudgetFile,
    loading,
    error,
  };

  return (
    <BudgetFileContextProvider.Provider value={value}>
      <AnnouncementRegion />
      {children}
    </BudgetFileContextProvider.Provider>
  );
};

// Optional: Export a hook that provides budget file colors for UI theming
export const useBudgetFileTheme = () => {
  const { currentBudgetFile } = useBudgetFile();
  
  return {
    primaryColor: currentBudgetFile?.color_theme || '#2da26b',
    contextType: currentBudgetFile?.context_type || 'personal',
    displayName: currentBudgetFile?.name || 'Budget',
  };
};